<template>
  <main class="floor">
    <div
      v-for="(row, index) in rows"
      :key="index"
      class="floor__row"
      :class="{ 'floor__row--lone': row.lone }"
      :style="{ justifyContent: row.justify }"
    >
      <router-link
        v-for="figure in row.figures"
        :key="figure.slug"
        :to="`/produkt/${figure.slug}`"
        class="floor__figure"
        :style="figureStyle(figure)"
      >
        <img
          :src="figure.flat.src"
          :alt="figure.flat.alt"
          :width="figure.flat.w"
          :height="figure.flat.h"
          loading="lazy"
        >
      </router-link>
    </div>
  </main>
</template>

<script>
import { getProduct } from '@/config/products.js';

/* Two to a row, alternating sides: odd figures take the left of the floor,
   even ones the right. Each keeps its own angle, size and vertical drop, so
   the pairs read as people lying down rather than as a grid.
   `size` is a multiplier on a shared height, not a width — the images are
   cropped tight to each silhouette, so only height makes the people
   themselves come out the same size.
   `drop` is a percentage of the figure's own box, so a figure holds its
   position relative to itself at every viewport width.
   `rotate` is the desktop angle; the phone damps it to 0.55 of itself to buy
   back the room the overhang costs. `rotateMobile` and `shiftMobile` opt one
   figure out of what the phone would otherwise do — only declare them where the
   two viewports genuinely want different placements, so the other four cannot
   drift apart from a second list. Desktop has no horizontal offset at all: the
   lone figure closes the floor on the centre line. */
const PLACEMENT = [
  { slug: 'polo-red', rotate: -13, size: 0.98, drop: 0 },
  { slug: 'polo-blue', rotate: 9, size: 1.24, drop: 6 },
  { slug: 'tanktop', rotate: 16, size: 1.14, drop: -30 },
  { slug: 'shirt-white', rotate: -5, size: 1.1, drop: 10 },
  {
    slug: 'shirt-black', rotate: -8, rotateMobile: -15.4, shiftMobile: -37.5,
    size: 1.0, drop: -38,
  },
];

export default {
  name: 'Home',
  computed: {
    rows() {
      const figures = PLACEMENT.map((placement) => ({
        ...placement,
        flat: getProduct(placement.slug).flat,
      }));

      const rows = [];
      for (let i = 0; i < figures.length; i += 2) {
        // A lone figure closes the floor on the centre line rather than
        // holding the left slot the alternation happens to land on, and on
        // desktop it climbs into the gap the row above left in its middle.
        const lone = !figures[i + 1];
        rows.push({
          lone,
          justify: lone ? 'center' : 'space-between',
          figures: figures.slice(i, i + 2),
        });
      }
      return rows;
    },
  },
  methods: {
    figureStyle(figure) {
      const style = {
        '--rotate': `${figure.rotate}deg`,
        '--size': figure.size,
        '--drop': `${figure.drop}%`,
      };

      // Written only where PLACEMENT asks for it: an inline custom property
      // beats the stylesheet, so setting it for everyone would silently
      // override the damping default the mobile rule falls back on.
      if (figure.rotateMobile !== undefined) {
        style['--rotate-mobile'] = `${figure.rotateMobile}deg`;
      }

      if (figure.shiftMobile !== undefined) {
        style['--shift-mobile'] = `${figure.shiftMobile}%`;
      }

      return style;
    },
  },
};
</script>

<style scoped>
.floor {
  --figure-height: min(42vw, 620px);

  display: flex;
  flex-direction: column;
  padding: max(13vh, var(--header-clearance)) 8vw 5vh;
  gap: 9vh;
}

/* The rows are laid down one after another, not the figures: a figure already
   carries its own transform — the drop, the angle, the hover lift — and an
   animation on it would have to restate the whole chain to avoid wiping it.
   The row around it is free. */
.floor__row {
  display: flex;
  align-items: flex-start;
  gap: 5vw;

  animation: lay-down 0.65s cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

.floor__row:nth-child(2) {
  animation-delay: 0.12s;
}

.floor__row:nth-child(3) {
  animation-delay: 0.24s;
}

/* A short drop and a fade, the same move the plates on the other pages
   settle with — the people are being laid on the carpet, not flown in. */
@keyframes lay-down {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
}

/* The pair above is pushed to opposite edges, so its middle is empty floor.
   Climbing into it makes three people read as one row across the carpet
   instead of two and a straggler. Desktop only: below 760px the rows are
   already nearly full width and there is no middle to climb into.
   The -9vh cancels the flex gap, the second term does the actual lifting. */
@media (min-width: 761px) {
  .floor__row--lone {
    margin-top: calc(-9vh - min(42vw, 620px) * 0.82);
  }
}

.floor__figure {
  --lift: 0px;
  --rise: 1;

  display: block;
  height: calc(var(--size) * var(--figure-height));
  /* --lift before the rotation so it reads as straight up off the floor rather
     than as a slide along the body; --rise after it, where the angle no longer
     matters. Hovering only touches those two, so the placement survives. */
  transform:
    translateY(var(--drop))
    translateY(var(--lift))
    rotate(var(--rotate))
    scale(var(--rise));
  transition: transform 0.55s cubic-bezier(0.2, 0.6, 0.25, 1);
}

.floor__figure img {
  display: block;
  height: 100%;
  width: auto;
  /* Two shadows: a tight dark one for where the body presses into the weave,
     a wide soft one for the light coming from above. Without the tight one
     they read as stickers rather than people lying down. */
  filter:
    drop-shadow(0 3px 4px rgba(40, 28, 18, 0.52))
    drop-shadow(0 20px 28px rgba(40, 28, 18, 0.42));
  transition: filter 0.3s ease;
}

/* Lifting the shadow reads as the figure rising off the floor towards you. */
.floor__figure:hover img,
.floor__figure:focus-visible img {
  filter:
    drop-shadow(0 6px 8px rgba(40, 28, 18, 0.46))
    drop-shadow(0 34px 44px rgba(40, 28, 18, 0.44));
}

/* The body making the same move the shadow describes. Behind hover:hover,
   because a tap on a phone latches :hover and would leave the figure hanging
   off the floor until something else is touched. */
@media (hover: hover) {
  .floor__figure:hover {
    --lift: -14px;
    --rise: 1.035;
  }
}

.floor__figure:focus-visible {
  --lift: -14px;
  --rise: 1.035;

  outline: 2px solid #2e4057;
  outline-offset: 10px;
}

/* Pairs stay side by side on phones too. Two figures plus their rotation
   overhang have to fit one screen width, and the angles cost the most: a
   16-degree turn sweeps a third of the figure's own height past its box.
   Damping the angles buys back enough room for the people to stay legible. */
@media (max-width: 760px) {
  .floor {
    --figure-height: min(80vw, 350px);

    /* Wider than it looks it needs: pushed to opposite edges, each figure's
       rotation overhang lands in this padding instead of off-screen. */
    padding: max(12vh, var(--header-clearance)) 7.5vw 4vh;
    gap: 7vh;
  }

  .floor__row {
    gap: 2vw;
  }

  .floor__figure {
    /* The damping ratio lives here as a default, so a figure that renders at a
       different angle on a phone can say so without the other four carrying a
       second number they would only have to keep in step. Same for the
       horizontal offset, which desktop does not use at all. */
    --rotate-mobile: calc(var(--rotate) * 0.55);
    --shift-mobile: 0%;

    /* Before the rotation, so the offset runs along the screen rather than
       along the body. */
    transform:
      translateX(var(--shift-mobile))
      translateY(var(--drop))
      translateY(var(--lift))
      rotate(var(--rotate-mobile))
      scale(var(--rise));
  }
}

@media (prefers-reduced-motion: reduce) {
  .floor__row {
    animation: none;
  }

  .floor__figure,
  .floor__figure img {
    transition: none;
  }

  /* Last in the file so it outranks the hover rules it cancels. */
  .floor__figure:hover,
  .floor__figure:focus-visible {
    --lift: 0px;
    --rise: 1;
  }
}
</style>
